/**
 * Fallback text extraction from PDF content streams (when pdf.js getTextContent is empty).
 */

const tokenizePdfContent = (content) => {
    const tokens = [];
    let i = 0;
    const len = content.length;

    const skipWs = () => {
        while (i < len && /[\0\t\n\f\r ]/.test(content[i])) i += 1;
    };

    const readString = () => {
        if (content[i] !== "(") return null;
        i += 1;
        let out = "";
        let depth = 1;
        while (i < len && depth > 0) {
            const ch = content[i];
            if (ch === "\\") {
                i += 1;
                if (i >= len) break;
                const esc = content[i];
                if (esc === "n") out += "\n";
                else if (esc === "r") out += "\r";
                else if (esc === "t") out += "\t";
                else out += esc;
                i += 1;
                continue;
            }
            if (ch === "(") depth += 1;
            if (ch === ")") {
                depth -= 1;
                if (depth === 0) {
                    i += 1;
                    break;
                }
            }
            if (depth > 0) out += ch;
            i += 1;
        }
        return out;
    };

    const readHexString = () => {
        if (content[i] !== "<" || content[i + 1] === "<") return null;
        i += 1;
        let hex = "";
        while (i < len && content[i] !== ">") {
            if (!/[\0\t\n\f\r ]/.test(content[i])) hex += content[i];
            i += 1;
        }
        if (content[i] === ">") i += 1;
        let out = "";
        for (let h = 0; h + 1 < hex.length; h += 2) {
            out += String.fromCharCode(parseInt(hex.slice(h, h + 2), 16));
        }
        return out;
    };

    const readNumberOrName = () => {
        const start = i;
        if (content[i] === "/") {
            i += 1;
            while (i < len && !/[\0\t\n\f\r \[\]()<>]/.test(content[i])) i += 1;
            return content.slice(start, i);
        }
        while (i < len && !/[\0\t\n\f\r \[\]()<>]/.test(content[i])) i += 1;
        return content.slice(start, i);
    };

    while (i < len) {
        skipWs();
        if (i >= len) break;
        if (content[i] === "(") {
            tokens.push(readString());
            continue;
        }
        if (content[i] === "<" && content[i + 1] !== "<") {
            tokens.push(readHexString());
            continue;
        }
        if (content[i] === "[") {
            i += 1;
            const arr = [];
            while (i < len) {
                skipWs();
                if (content[i] === "]") {
                    i += 1;
                    break;
                }
                if (content[i] === "(") arr.push(readString());
                else if (content[i] === "<" && content[i + 1] !== "<") arr.push(readHexString());
                else {
                    const t = readNumberOrName();
                    if (t) arr.push(t);
                }
            }
            tokens.push(arr);
            continue;
        }
        const token = readNumberOrName();
        if (token) tokens.push(token);
    }
    return tokens;
};

const multiplyMatrix = (m1, m2) => {
    const [a1, b1, c1, d1, e1, f1] = m1;
    const [a2, b2, c2, d2, e2, f2] = m2;
    return [
        a1 * a2 + b1 * c2,
        a1 * b2 + b1 * d2,
        c1 * a2 + d1 * c2,
        c1 * b2 + d1 * d2,
        e1 * a2 + f1 * c2 + e2,
        e1 * b2 + f1 * d2 + f2,
    ];
};

const decodeFlateBytes = async (deflated) => {
    if (typeof DecompressionStream === "undefined") {
        return deflated;
    }
    for (const format of ["deflate", "deflate-raw"]) {
        try {
            const stream = new Blob([deflated]).stream().pipeThrough(
                new DecompressionStream(format)
            );
            const buffer = await new Response(stream).arrayBuffer();
            return new Uint8Array(buffer);
        } catch {
            // try next
        }
    }
    return deflated;
};

const latin1FromBytes = (bytes) => {
    let out = "";
    for (let j = 0; j < bytes.length; j += 1) {
        out += String.fromCharCode(bytes[j]);
    }
    return out;
};

const collectStreamBodies = (pdfLatin1) => {
    const bodies = [];
    const streamMarker = "stream";
    const endMarker = "endstream";
    let searchFrom = 0;

    while (searchFrom < pdfLatin1.length) {
        const streamIdx = pdfLatin1.indexOf(streamMarker, searchFrom);
        if (streamIdx < 0) break;
        let dataStart = streamIdx + streamMarker.length;
        if (pdfLatin1[dataStart] === "\r") dataStart += 1;
        if (pdfLatin1[dataStart] === "\n") dataStart += 1;
        const endIdx = pdfLatin1.indexOf(endMarker, dataStart);
        if (endIdx < 0) break;
        let dataEnd = endIdx;
        while (dataEnd > dataStart && /[\r\n]/.test(pdfLatin1[dataEnd - 1])) {
            dataEnd -= 1;
        }
        const headerStart = Math.max(0, streamIdx - 800);
        const header = pdfLatin1.slice(headerStart, streamIdx);
        const isFlate = /\/FlateDecode/i.test(header);
        const raw = pdfLatin1.slice(dataStart, dataEnd);
        const rawBytes = new Uint8Array(raw.length);
        for (let j = 0; j < raw.length; j += 1) {
            rawBytes[j] = raw.charCodeAt(j) & 0xff;
        }
        bodies.push({ rawBytes, isFlate });
        searchFrom = endIdx + endMarker.length;
    }
    return bodies;
};

const parseTextItemsFromContent = (content) => {
    const tokens = tokenizePdfContent(content);
    const items = [];
    let ctm = [1, 0, 0, 1, 0, 0];
    let tm = [1, 0, 0, 1, 0, 0];
    const ctmStack = [];
    const tmStack = [];
    let fontSize = 12;

    const pushText = (str) => {
        const text = String(str ?? "");
        if (!text.trim()) return;
        const matrix = multiplyMatrix(ctm, tm);
        const scaleY = Math.hypot(matrix[2], matrix[3]) || fontSize;
        const scaleX = Math.hypot(matrix[0], matrix[1]) || fontSize;
        const size = Math.max(scaleY, scaleX, fontSize);
        items.push({
            str: text,
            transform: [size, 0, 0, size, matrix[4], matrix[5]],
            width: Math.max(size * 0.45 * text.length, size),
            height: size * 1.15,
        });
    };

    const readNumber = (token) => {
        const n = Number(token);
        return Number.isFinite(n) ? n : 0;
    };

    for (let idx = 0; idx < tokens.length; idx += 1) {
        const token = tokens[idx];
        if (token === "q") {
            ctmStack.push(ctm);
            tmStack.push(tm);
            continue;
        }
        if (token === "Q") {
            ctm = ctmStack.pop() || [1, 0, 0, 1, 0, 0];
            tm = tmStack.pop() || [1, 0, 0, 1, 0, 0];
            continue;
        }
        if (token === "cm" && idx >= 6) {
            ctm = multiplyMatrix(ctm, tokens.slice(idx - 6, idx).map(readNumber));
            continue;
        }
        if (token === "Tm" && idx >= 6) {
            tm = tokens.slice(idx - 6, idx).map(readNumber);
            continue;
        }
        if ((token === "Td" || token === "TD") && idx >= 2) {
            tm = multiplyMatrix(tm, [
                1,
                0,
                0,
                1,
                readNumber(tokens[idx - 2]),
                readNumber(tokens[idx - 1]),
            ]);
            continue;
        }
        if (token === "T*") {
            tm = multiplyMatrix(tm, [1, 0, 0, 1, 0, -fontSize]);
            continue;
        }
        if (token === "Tf" && idx >= 2) {
            fontSize = readNumber(tokens[idx - 1]) || fontSize;
            continue;
        }
        if (token === "Tj" && idx >= 1) {
            pushText(tokens[idx - 1]);
            continue;
        }
        if (token === "TJ" && idx >= 1 && Array.isArray(tokens[idx - 1])) {
            pushText(
                tokens[idx - 1]
                    .filter((part) => typeof part === "string")
                    .join("")
            );
            continue;
        }
        if (token === "'" && typeof tokens[idx - 1] === "string") {
            tm = multiplyMatrix(tm, [1, 0, 0, 1, 0, -fontSize]);
            pushText(tokens[idx - 1]);
            continue;
        }
        if (token === '"' && idx >= 3) {
            const aw = readNumber(tokens[idx - 3]);
            const ac = readNumber(tokens[idx - 2]);
            const str = tokens[idx - 1];
            tm = multiplyMatrix(tm, [1, 0, 0, 1, -(aw + ac), -fontSize]);
            if (typeof str === "string") pushText(str);
            continue;
        }
    }

    return items;
};

export const extractTextItemsFromPdfBytes = async (pdfBytes) => {
    const bytes =
        pdfBytes instanceof Uint8Array ? pdfBytes : new Uint8Array(pdfBytes);
    const latin1 = latin1FromBytes(bytes);
    const streams = collectStreamBodies(latin1);
    const items = [];

    for (const stream of streams) {
        let decoded = stream.rawBytes;
        if (stream.isFlate) {
            decoded = await decodeFlateBytes(stream.rawBytes);
        }
        const content = latin1FromBytes(decoded);
        if (!/(Tj|TJ|'|")/.test(content)) continue;
        items.push(...parseTextItemsFromContent(content));
    }

    return items;
};
